import React, { useEffect } from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import Portal from '@mui/material/Portal';

import Snackbar from 'shared/atoms/Snackbar';

import { firstLetterCaps } from 'shared/utils';
import theme from 'shared/themes/default';

import ShowCourseData from './components/ShowCourseData';
import ShowGroupsData from './components/ShowGroupsData';
import ShowOrganizationsData from './components/ShowOrganizationsData';
import ShowPlaylistData from './components/ShowPlaylistData';
import ShowUsersData from './components/ShowUsersData';
import WatchDataTabs from './components/WatchDataTabs';

import { useWatchData } from './Context';

const StyledBlock = styled.div`
  max-width: 100%;
  margin-top: ${theme.spacing.m2};
  ${theme.breakpoints.mobile} {
    margin-top: 0;
  }
`;

const mapToOption = (item, field = 'id') => ({
  value: item[field],
  label: item.name,
});

const WatchData = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const {
    allCoursesStats,
    allPlaylistsStats,
    courseSelected,
    groupSelected,
    handleHistory,
    isOrganizationManager,
    loadingCoursesWatchData,
    loadingGroups,
    loadingPlaylistsWatchData,
    loadingUsersWatchData,
    onSelectItem,
    organizationGroupsStats,
    organizationSelectedId,
    playlistSelected,
    setWatchDataError,
    watchDataError,
    view,
  } = useWatchData();

  const globalTabs = [
    { key: 'users', label: t('watchData.users') },
    { key: 'courses', label: t('watchData.courses') },
  ];
  const managerTabs = [
    { key: 'groups', label: t('administrateUsers.groups') },
    { key: 'playlists', label: t('playlist.playlists') },
  ];

  let tabsConfig = [];
  if (!organizationSelectedId) {
    tabsConfig = [...globalTabs];
  } else if (isOrganizationManager) {
    tabsConfig = [...managerTabs];
  } else {
    // isAdmin + Org selected
    tabsConfig = [...globalTabs, ...managerTabs];
  }

  useEffect(() => {
    if (!view || !tabsConfig.find(({ key }) => key === view)) {
      if (pathname.includes('administrate/dashboard')) {
        if (isOrganizationManager) {
          // Default view for managers is groups
          handleHistory('view=groups');
        } else {
          handleHistory('view=users');
        }
      }
    }
  }, [view]);

  let optionsToSelect = [];
  let initValue = null;

  if (view === 'courses') {
    optionsToSelect = allCoursesStats?.map((item) =>
      mapToOption(item, 'courseId')
    );
    initValue = courseSelected && mapToOption(courseSelected, 'courseId');
  } else if (view === 'groups') {
    optionsToSelect = organizationGroupsStats?.map((item) => mapToOption(item));
    initValue = groupSelected && mapToOption(groupSelected);
  } else if (view === 'playlists') {
    optionsToSelect = allPlaylistsStats?.map((item) => mapToOption(item));
    initValue = playlistSelected && mapToOption(playlistSelected);
  }

  const searchLoading =
    (view === 'users' && loadingUsersWatchData) ||
    (view === 'courses' && loadingCoursesWatchData) ||
    (view === 'groups' && loadingGroups) ||
    (view === 'playlists' && loadingPlaylistsWatchData) ||
    false;

  const selectedTabHandler = (tab) => {
    if (view !== tab) {
      handleHistory(`view=${tab}`);
    }
  };

  return (
    <StyledBlock>
      <WatchDataTabs
        title={t('watchData.showActivityFor')}
        tabsConfig={tabsConfig}
        selectedTab={view || ''}
        setSelectedTab={selectedTabHandler}
        searchPlaceholder={t(`watchData.searchFor${firstLetterCaps(view)}`)}
        searchingInTitle={t(`watchData.searchingIn${firstLetterCaps(view)}`)}
        onChangeSearch={(item) => onSelectItem(item?.value)}
        onClearSearch={() => onSelectItem()}
        searchOptions={optionsToSelect
          ?.filter((item) => item.label)
          .sort((a, b) => a.label.localeCompare(b.label))}
        preSelectedValue={initValue}
        getOptionKey={(option) => option.value}
        searchDisabled={view === 'users'}
        searchLoading={searchLoading}
      >
        {view === 'courses' && <ShowCourseData />}
        {view === 'users' &&
          (organizationSelectedId ? (
            <ShowUsersData />
          ) : (
            <ShowOrganizationsData />
          ))}
        {view === 'groups' && <ShowGroupsData />}
        {view === 'playlists' && <ShowPlaylistData />}
      </WatchDataTabs>
      <Portal>
        <Snackbar
          type="error"
          message={t('errors.requestFailed')}
          open={watchDataError}
          onClose={() => setWatchDataError(false)}
        />
      </Portal>
    </StyledBlock>
  );
};

export default WatchData;
